import { useEffect } from 'react';
import { Clock, Mail } from 'lucide-react';
import { api, authApi, productsApi } from '@/lib/api-client';
import { useAuthStore, type AuthUser, type AuthTenant } from '@/stores/auth-store';
import { useQueryClient } from '@tanstack/react-query';

export function PendingActivationPage({ locationId }: { locationId?: string }) {
  const { setAuth } = useAuthStore();
  const queryClient = useQueryClient();

  // Poll every 10s to detect when super admin activates the tenant or location
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        if (locationId) {
          // Location-level: poll locations endpoint and check if this location is now active
          const res = await productsApi.get<{ data: any[] }>('/locations');
          const loc = res.data.find((l: any) => l.id === locationId);
          if (loc?.isActive) {
            queryClient.invalidateQueries({ queryKey: ['locations'] });
          }
        } else {
          // Tenant-level: poll auth/me + tenants/me
          const userRes = await authApi.get<{ data: AuthUser }>('/auth/me');
          const user = userRes.data;
          if (user.tenantId) {
            const tenantRes = await api.get<{ data: AuthTenant | null }>('/tenants/me');
            if (tenantRes.data?.isActive) {
              setAuth(user, tenantRes.data);
            }
          }
        }
      } catch {
        // ignore — user might have been logged out
      }
    }, 10_000);
    return () => clearInterval(interval);
  }, [setAuth, locationId, queryClient]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="max-w-md lg:max-w-[717px] text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
          <Clock className="h-8 w-8 text-amber-600" />
        </div>
        <h1 className="text-2xl font-bold text-foreground">Account Pending Activation</h1>
        <p className="mt-3 text-muted-foreground">
          Our team is reviewing your business information. Within the next 1-2 business days you will receive an email
          notification once your account has been activated and is ready to use. <br />
          <br />
          Kindly ensure to email us your business void check copy and your driver's license front and back copy to{' '}
          <a href="mailto:support@qpaymentz.com" className="text-primary underline hover:text-primary/80">
            support@qpaymentz.com
          </a>
          .<br />
          <br />
          If your business has an annual card volume exceeding $1 Million, please provide your recent 3 month's bank and
          processing statements. Our Sales/Boarding team will contact you soon to discuss the boarding process.
        </p>
        <div className="mt-6 rounded-lg border border-border bg-white p-4">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <Mail className="h-5 w-5 flex-shrink-0" />
            <span>Check your inbox for updates, or contact support if you have questions.</span>
          </div>
          <div className="text-sm text-muted-foreground flex-shrink-0">
            <span>
              <a href="mailto:support@qpaymentz.com" className="text-primary underline hover:text-primary/80">
                support@qpaymentz.com
              </a>{' '}
              |{' '}
              <a href="tel:4088003155" className="text-primary underline hover:text-primary/80">
                408 800 3155
              </a>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
